import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PreflightReport } from '../../services/logmerge-api.service';
import { ProcessingStateService, ProcessingConfig } from '../../services/processing-state.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit {
  constructor(
    public state: ProcessingStateService,
    private auth: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Default the completion-email address to the signed-in account; the
    // user can still overwrite it or switch notifications off entirely.
    if (!this.state.notifyEmail) {
      this.state.notifyEmail = this.auth.getUserEmail();
    }

    // Arriving from a completion email (`/?job=<id>`): start the download
    // immediately, then strip the parameter so a refresh does not repeat it.
    const jobId = this.route.snapshot.queryParamMap.get('job');
    if (jobId) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { job: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
      this.state.downloadJobById(jobId);
    }
  }

  // Template-facing getters (so existing HTML bindings keep working)
  get config(): ProcessingConfig { return this.state.config; }
  get selectedFiles(): File[] { return this.state.selectedFiles; }
  get selectedLogSuiteFile(): File | null { return this.state.selectedLogSuiteFile; }
  get isPreflighting(): boolean { return this.state.isPreflighting; }
  get preflightReport(): PreflightReport | null { return this.state.preflightReport; }
  get showScraperModal(): boolean { return this.state.showScraperModal; }
  get isProcessing(): boolean { return this.state.isProcessing; }
  get progress(): number { return this.state.progress; }
  get statusMessage(): string { return this.state.statusMessage; }
  get isStartProcessingDisabled(): boolean { return this.state.isStartProcessingDisabled; }
  get isDownloading(): boolean { return this.state.isDownloading; }
  get isUploading(): boolean { return this.state.isUploading; }
  get uploadPercent(): number { return this.state.uploadPercent; }
  get uploadedFileCount(): number { return this.state.uploadedFileCount; }
  get uploadTotalFileCount(): number { return this.state.uploadTotalFileCount; }

  get notifyByEmail(): boolean { return this.state.notifyByEmail; }
  set notifyByEmail(value: boolean) { this.state.notifyByEmail = value; }
  get notifyEmail(): string { return this.state.notifyEmail; }
  set notifyEmail(value: string) { this.state.notifyEmail = value; }

  // Event handlers
  onFilesSelected(event: Event): void {
    // Ignore new file selections while a job is running so the current
    // session state cannot be mutated by a mis-click on Browse Files.
    if (this.isProcessing) return;
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.state.setSelectedFiles(Array.from(input.files), 'browse');
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Same lockout as onFilesSelected -- ignore drops during processing.
    if (this.isProcessing) return;
    if (event.dataTransfer?.files) {
      const files = Array.from(event.dataTransfer.files).filter(
        f => f.name.toLowerCase().endsWith('.las')
      );
      this.state.setSelectedFiles(files, 'drop');
    }
  }

  onLogSuiteFileSelected(event: Event): void {
    if (this.isProcessing) return;
    const input = event.target as HTMLInputElement;
    this.state.setLogSuiteFile(input.files?.[0] || null);
  }

  onSourceFolderSelected(event: Event): void {
    if (this.isProcessing) return;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const rel = (input.files[0] as any).webkitRelativePath as string;
      this.state.config.source_folder = rel.split('/')[0];
    }
  }

  // ---------------------------------------------------------------------------
  // Pipeline stage cascade
  // ---------------------------------------------------------------------------
  // The pipeline is linear: Clean -> Resample -> Combine -> Merge, and every
  // stage reads the previous stage's output. So a later stage can only run if
  // all earlier stages run too. The UI enforces this: checking a stage
  // auto-checks + locks every earlier stage; unchecking a stage clears the
  // stages after it. Locked prerequisites render checked + disabled so the
  // user sees *why* they are on but cannot turn them off.

  /** Ordered stage keys from first to last in the pipeline. */
  private readonly stageOrder: Array<'clean' | 'resample' | 'combine' | 'merge'> = [
    'clean', 'resample', 'combine', 'merge'
  ];

  /** Index of the highest currently-checked stage (-1 if none). */
  private highestCheckedStageIndex(): number {
    let highest = -1;
    this.stageOrder.forEach((stage, idx) => {
      if (this.config[stage]) {
        highest = idx;
      }
    });
    return highest;
  }

  /**
   * A stage checkbox is locked (checked + disabled) when a LATER stage is
   * selected, because that later stage requires this one as a prerequisite.
   */
  isStageLocked(stage: 'clean' | 'resample' | 'combine' | 'merge'): boolean {
    if (this.isProcessing) return true; // everything locks during a run
    const idx = this.stageOrder.indexOf(stage);
    return idx < this.highestCheckedStageIndex();
  }

  /**
   * Handle a stage checkbox toggle and cascade the linear dependency:
   *   * Checking a stage force-checks every earlier stage.
   *   * Unchecking a stage clears every later stage (they can no longer run).
   */
  onStageToggle(stage: 'clean' | 'resample' | 'combine' | 'merge', checked: boolean): void {
    if (this.isProcessing) return;
    const idx = this.stageOrder.indexOf(stage);

    if (checked) {
      // Enable this stage and every prerequisite before it.
      for (let i = 0; i <= idx; i++) {
        this.config[this.stageOrder[i]] = true;
      }
    } else {
      // Disable this stage and everything downstream of it.
      for (let i = idx; i < this.stageOrder.length; i++) {
        this.config[this.stageOrder[i]] = false;
      }
    }
  }

  startProcessing(): void { this.state.startProcessing(); }
  cancel(): void { this.state.cancel(); }
  downloadResults(): void { this.state.downloadResults(); }
  confirmScraperInsights(): void { this.state.confirmScraperInsights(); }
  cancelScraperInsights(): void { this.state.cancelScraperInsights(); }
}
